import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHeaderFeed } from '@/hooks/useHeaderFeed';
import { useIsMobile } from '@/hooks/use-mobile';
import { BreakingPill } from './BreakingPill';
import { NewsTicker } from './NewsTicker';
import { WeatherChip } from './WeatherChip';
import { WeatherSearchModal } from './WeatherSearchModal';
import { MobileLatestSheet } from './MobileLatestSheet';
import { Button } from '@/components/ui/button';

const TICKER_DISMISS_KEY = 'geniebot-ticker-dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

interface HeaderInfoBarProps {
  className?: string;
}

export function HeaderInfoBar({ className }: HeaderInfoBarProps) {
  const [weatherModalOpen, setWeatherModalOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      const stored = localStorage.getItem(TICKER_DISMISS_KEY);
      if (!stored) return false;
      const dismissedAt = parseInt(stored, 10);
      return Date.now() - dismissedAt < DISMISS_DURATION_MS;
    } catch {
      return false;
    }
  });
  const isMobile = useIsMobile();
  const {
    data,
    loading,
    weatherLocation,
    setWeatherLocation,
    useCurrentLocation,
  } = useHeaderFeed();
  
  const items = data?.items || [];
  const hasBreaking = items.some(item => item.category === 'breaking');
  const hasWeatherAlerts = data?.weather?.alerts && data.weather.alerts.length > 0;

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    try {
      localStorage.setItem(TICKER_DISMISS_KEY, Date.now().toString());
    } catch {
      // Ignore storage errors
    }
    // Update CSS variable so chat layout adjusts
    document.documentElement.style.setProperty('--total-header-height', 'var(--navbar-height)');
  }, []);

  // On mount, ensure header height is correct based on dismiss state
  useEffect(() => {
    if (isDismissed) {
      document.documentElement.style.setProperty('--total-header-height', 'var(--navbar-height)');
    } else {
      document.documentElement.style.setProperty('--total-header-height', 'calc(var(--header-info-height) + var(--navbar-height))');
    }
  }, [isDismissed]);

  if (isDismissed) return null;
  
  return (
    <>
      <div
        className={cn(
          "w-full h-9 md:h-10 bg-muted/30 border-b border-border/50",
          "flex items-center gap-2 px-3 md:px-4",
          hasWeatherAlerts && "border-b-destructive/50",
          className
        )}
        role="region"
        aria-label="News and weather information bar"
      >
        {/* Breaking Pill - Left */}
        <BreakingPill visible={hasBreaking} />
        
        {/* News Ticker - Center (Desktop) / Mobile Sheet Trigger */}
        {isMobile ? (
          <MobileLatestSheet items={items} />
        ) : (
          <NewsTicker items={items} className="mx-2" />
        )}
        
        {/* Weather Chip */}
        <WeatherChip
          weather={data?.weather || null}
          loading={loading}
          onClick={() => setWeatherModalOpen(true)}
          className="ml-auto"
        />

        {/* Dismiss button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={handleDismiss}
          aria-label="Dismiss news ticker for 24 hours"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      
      {/* Weather Search Modal */}
      <WeatherSearchModal
        open={weatherModalOpen}
        onOpenChange={setWeatherModalOpen}
        onSelectLocation={setWeatherLocation}
        onUseCurrentLocation={useCurrentLocation}
        currentLocation={weatherLocation}
        alerts={data?.weather?.alerts}
      />
    </>
  );
}
